/**
 * Low-level utility functions for video template operations
 * Pure functions with no React dependencies
 */

import {
  CompositionPlacement,
  CompositionType,
} from '@src/components/sidepanel/CompositionSettings/types';
import { transformKaraokeConfigToTemplate } from '@src/services/videoTemplate/VideoTemplateTransformers';
import { AspectRatio } from '@src/types.ts/video';
import { mergeWith } from 'lodash';

import type {
  AspectConfiguration,
  CompositionTemplate,
} from '../CompositionSettings/types';
import type {
  VideoTemplate,
  VideoTemplateConfig,
  VideoTemplateConfigured,
} from './types';
import type { VideoTemplateElementType } from './VideoTemplatesContext';
import type { KaraokeTemplate } from '@src/services/karaoke/types';

/**
 * Replaces the composition template for a card configuration.
 * If the configuration doesn't exist, creates a new one with the composition.
 */
export function replaceCardComposition(
  config: VideoTemplateConfig,
  elementType: 'introCard' | 'endCard' | 'nameCard' | 'hook',
  newComposition: CompositionTemplate,
): VideoTemplateConfig {
  const configKey =
    elementType === 'introCard'
      ? 'introCardConfiguration'
      : elementType === 'endCard'
        ? 'endCardConfiguration'
        : elementType === 'hook'
          ? 'hookConfiguration'
          : 'namecardConfiguration';

  const existingConfig = config[configKey];

  // Create new configuration if it doesn't exist (add case)
  if (!existingConfig) {
    return {
      ...config,
      [configKey]: {
        enabled: true,
        compositionTemplate: newComposition,
      },
    };
  }

  return {
    ...config,
    [configKey]: {
      ...existingConfig,
      compositionTemplate: newComposition,
    },
  };
}

/**
 * Replaces the logo URL in the configuration.
 * If the configuration doesn't exist, creates a new one with the logo URL.
 */
export function replaceLogoUrl(
  config: VideoTemplateConfig,
  newUrl: string,
): VideoTemplateConfig {
  // Create new configuration if it doesn't exist (add case)
  if (!config.logoConfiguration) {
    return {
      ...config,
      logoConfiguration: {
        enabled: true,
        logo: {
          id: '',
          url: newUrl,
        },
        opacity: null,
        placementX: null,
        placementY: null,
        width: null,
        height: null,
        fit: null,
      },
    };
  }

  return {
    ...config,
    logoConfiguration: {
      ...config.logoConfiguration,
      logo: {
        id: config.logoConfiguration.logo?.id || '',
        url: newUrl,
      },
    },
  };
}

/**
 * Creates an empty VideoTemplateConfig with all fields set to null
 */
function createEmptyConfig(): VideoTemplateConfig {
  return {
    logoConfiguration: null,
    introCardConfiguration: null,
    endCardConfiguration: null,
    captionsConfiguration: null,
    namecardConfiguration: null,
    hookConfiguration: null,
  };
}

/**
 * Creates a modified template with a replaced element ID for a specific type
 * Note: This returns a promise because we may need to fetch the composition template
 */
export async function createTemplateWithReplacedElement(
  template: VideoTemplateConfigured,
  elementType: VideoTemplateElementType,
  replacedElementData: string,
  getCompositionById: (id: string) => CompositionTemplate | undefined,
): Promise<VideoTemplateConfigured> {
  // Deep clone to avoid mutating the original
  // If no configuration exists, create an empty one (for "add" case)
  const modifiedTemplate: VideoTemplateConfigured = {
    ...template,
    configuration: template.configuration
      ? { ...template.configuration }
      : createEmptyConfig(),
  };

  // Handle composition slots (introCard, endCard, nameCard, and the hook —
  // PLAT-11034: this is what lets Replace SET a hook on a template that has none)
  if (['introCard', 'endCard', 'nameCard', 'hook'].includes(elementType)) {
    const newComposition = getCompositionById(replacedElementData);
    if (!newComposition) {
      console.warn('Could not find composition with ID', {
        replacedElementData,
      });
      return template;
    }
    modifiedTemplate.configuration = replaceCardComposition(
      modifiedTemplate.configuration!,
      elementType as 'introCard' | 'endCard' | 'nameCard' | 'hook',
      newComposition,
    );
  }
  // Handle logo element
  else if (elementType === 'logo') {
    modifiedTemplate.configuration = replaceLogoUrl(
      modifiedTemplate.configuration!,
      replacedElementData,
    );
  }
  // Captions are handled separately via applyKaraokeTemplate

  return modifiedTemplate;
}

/**
 * Gets the override configuration key based on aspect ratio
 */
export function getCurrentOverrideConfigurationKey(
  aspectRatio: AspectRatio,
): keyof Pick<VideoTemplate, 'override16x9' | 'override9x16' | 'override1x1'> {
  switch (aspectRatio) {
    case AspectRatio.AR_16_9:
      return 'override16x9';
    case AspectRatio.AR_9_16:
      return 'override9x16';
    case AspectRatio.AR_1_1:
      return 'override1x1';
  }
}

/**
 * Swaps each embedded card composition for the live record of the same id.
 *
 * A template's GraphQL response inlines a whole copy of its card compositions.
 * That copy comes from the Content Delivery API, which propagates asynchronously
 * after a CMA write — so right after "Set as Graphic Standard" it still holds the
 * pre-save placement, and no amount of cache-clearing or refetching fixes it.
 * `resolveComposition` reads the composition list instead, which is populated from
 * the CMA response and is therefore correct immediately.
 *
 * Returns new objects: when a template has no AR override, `merge` hands back
 * `commonConfiguration` by reference, so mutating here would corrupt the shared
 * template list. Falls back to the embedded copy whenever the id isn't found —
 * the list is empty unless the compositions tab is enabled, and global presets can
 * be excluded per customer type.
 */
function withLiveCardCompositions(
  configuration: VideoTemplateConfig | null,
  resolveComposition: (id: string) => CompositionTemplate | null | undefined,
): VideoTemplateConfig | null {
  if (!configuration) return null;

  const swap = <T extends { compositionTemplate?: CompositionTemplate | null }>(
    config: T | null,
  ): T | null => {
    const embedded = config?.compositionTemplate;
    if (!config || !embedded?.id) return config ?? null;
    const live = resolveComposition(embedded.id);
    if (!live || live === embedded) return config;
    return { ...config, compositionTemplate: live };
  };

  const namecardConfiguration = swap(configuration.namecardConfiguration);
  const introCardConfiguration = swap(configuration.introCardConfiguration);
  const endCardConfiguration = swap(configuration.endCardConfiguration);

  // Same object when nothing was swapped, so callers memoizing on identity see no
  // spurious change.
  if (
    namecardConfiguration === configuration.namecardConfiguration &&
    introCardConfiguration === configuration.introCardConfiguration &&
    endCardConfiguration === configuration.endCardConfiguration
  ) {
    return configuration;
  }
  return {
    ...configuration,
    namecardConfiguration,
    introCardConfiguration,
    endCardConfiguration,
  };
}

/**
 * Merge a raw VideoTemplate with its AR-specific overrides to produce a
 * fully configured template ready for application.
 *
 * `resolveComposition` is optional so callers without access to the composition
 * list keep the embedded copies; pass it wherever freshness matters.
 */
export function configureTemplateForAspectRatio(
  template: VideoTemplate,
  aspectRatio: AspectRatio,
  resolveComposition?: (id: string) => CompositionTemplate | null | undefined,
): VideoTemplateConfigured {
  const overrideKey = getCurrentOverrideConfigurationKey(aspectRatio);
  const merged = mergeConfiguration(
    template.commonConfiguration,
    template[overrideKey],
    aspectRatio,
  );
  // Before overrideConfigurableValues, which merges placementOverride INTO
  // compositionTemplate[currentKey] — swapping afterwards would discard it.
  const withLive = resolveComposition
    ? withLiveCardCompositions(merged, resolveComposition)
    : merged;
  const configuration = overrideConfigurableValues(withLive, aspectRatio);
  return {
    id: template.id,
    label: template.label,
    internalName: template.internalName,
    configuration,
    isGlobalPreset: template.isGlobalPreset,
  };
}

/**
 * Maps an aspect ratio enum to the corresponding CompositionTemplate placement key.
 */
export function getCompositionPlacementKeyByAspectRatio(
  aspectRatio: AspectRatio,
): keyof Pick<
  CompositionTemplate,
  'sixteenByNine' | 'nineBySixteen' | 'oneByOne'
> {
  switch (aspectRatio) {
    case AspectRatio.AR_16_9:
      return 'sixteenByNine';
    case AspectRatio.AR_9_16:
      return 'nineBySixteen';
    case AspectRatio.AR_1_1:
      return 'oneByOne';
  }
}

/**
 * PLAT-11034 — what a hook IS: an overlay composition on a clip-bound placement.
 *
 * Expressed once and used twice: the Replace picker offers only compositions that
 * satisfy it, and the apply path re-checks rather than trusting, because a
 * template's hook can also be set by hand in DatoCMS where nothing enforces it.
 * Lives here rather than in the service so the picker (a component) can read it
 * without importing a service.
 */
export function isHookComposition(
  template: Pick<CompositionTemplate, 'compositionType' | 'placement'>,
): boolean {
  return (
    template.compositionType === CompositionType.OVERLAY &&
    (template.placement === CompositionPlacement.CLIP ||
      template.placement === CompositionPlacement.CLIP_START)
  );
}

/**
 * Keys holding a whole DatoCMS record rather than a bag of settings. A record is
 * one indivisible thing: when an aspect ratio names its own, that record is the
 * answer in full, `null` fields included — those mean "this record has no
 * background / no shadow", not "unspecified". Merging field-by-field across two
 * of them produces a record that exists in no DatoCMS model, e.g. a karaoke
 * config wearing another one's `backgroundColor`.
 *
 * The link itself keeps the ordinary rule below: an override that names no
 * record (DatoCMS returns `null` for any unset link, so "absent" is
 * indistinguishable from "deliberately none") still inherits the base's.
 */
const ATOMIC_RECORD_KEYS = new Set(['captionsConfig']);

/**
 * Generic deep merge function that combines a base configuration with an override configuration.
 * The base configuration serves as the default, and override values replace base values when present.
 *
 * Rules:
 * - If override value is null or undefined, use base value
 * - If override value is present (not null/undefined), use override value
 * - Recursively merges nested objects, except for `ATOMIC_RECORD_KEYS`
 * - Arrays are treated as values (not merged, completely replaced if override exists)
 */
export const merge = <T extends Record<string, any> | null | undefined>(
  baseConfig: T | null,
  overrideConfig: T | null,
): T | null => {
  if (baseConfig === null && overrideConfig === null) return null;
  if (overrideConfig === null) return baseConfig;
  if (baseConfig === null) return overrideConfig;

  return mergeWith(
    {},
    baseConfig,
    overrideConfig,
    (baseValue, overrideValue, key) => {
      // If override is null/undefined, keep base value
      if (overrideValue === null || overrideValue === undefined) {
        return baseValue;
      }
      // If override is an array, replace completely (don't merge)
      if (Array.isArray(overrideValue)) {
        return overrideValue;
      }
      // A record the override names is taken whole — copied so callers can't
      // reach back into the stored template through it.
      if (ATOMIC_RECORD_KEYS.has(key)) {
        return { ...overrideValue };
      }
      // For objects and primitives, let lodash handle it (returns undefined = default behavior)
      return undefined;
    },
  );
};

/**
 * Overrides the namecard configuration's composition template placement based on aspect ratio.
 *
 * This function merges the original composition placement with any placement overrides
 * for the specified aspect ratio. If no composition template exists, returns the
 * configuration unchanged.
 */
export function overrideNamecardConfiguration(
  configuration: VideoTemplateConfig | null,
  aspectRatio: AspectRatio,
): VideoTemplateConfig | null {
  if (configuration === null) return null;

  // PLAT-11034 — both positionable overlay slots, same merge. The cards are
  // full-frame and carry no override.
  return mergePlacementOverrideIntoSlot(
    mergePlacementOverrideIntoSlot(
      configuration,
      'namecardConfiguration',
      aspectRatio,
    ),
    'hookConfiguration',
    aspectRatio,
  );
}

/**
 * Merges one slot's `placementOverride` over its composition's placement for the
 * current aspect ratio, so the created element lands where the template says.
 */
function mergePlacementOverrideIntoSlot(
  configuration: VideoTemplateConfig | null,
  slot: 'namecardConfiguration' | 'hookConfiguration',
  aspectRatio: AspectRatio,
): VideoTemplateConfig | null {
  if (configuration === null) return null;
  const compositionPlacementKey =
    getCompositionPlacementKeyByAspectRatio(aspectRatio);

  const slotConfiguration = configuration[slot];
  if (!slotConfiguration?.compositionTemplate) return configuration;

  const newCompositionPlacement = merge(
    slotConfiguration.compositionTemplate[compositionPlacementKey],
    slotConfiguration.placementOverride,
  );

  return {
    ...configuration,
    [slot]: {
      ...slotConfiguration,
      compositionTemplate: {
        ...slotConfiguration.compositionTemplate,
        [compositionPlacementKey]: newCompositionPlacement,
      },
    },
  };
}

/**
 * Merges base and override configurations with aspect ratio-specific namecard overrides
 */
export function mergeConfiguration(
  baseConfig: VideoTemplateConfig | null,
  overrideConfig: VideoTemplateConfig | null,
  currentAspectRatio: AspectRatio,
): VideoTemplateConfig | null {
  const mergedConfiguration = merge(baseConfig, overrideConfig);

  if (!mergedConfiguration) return null;
  const withNamecardOverride = overrideNamecardConfiguration(
    mergedConfiguration,
    currentAspectRatio,
  );
  return withNamecardOverride;
}

export function overrideConfigurableValues(
  configuration: VideoTemplateConfig | null,
  aspectRatio: AspectRatio,
): VideoTemplateConfig | null {
  if (configuration === null) return null;

  if (configuration.namecardConfiguration) {
    const currentKey = getCompositionPlacementKeyByAspectRatio(aspectRatio);
    const namecardConfiguration = configuration.namecardConfiguration;
    if (
      namecardConfiguration.placementOverride &&
      namecardConfiguration.compositionTemplate
    ) {
      const placement: AspectConfiguration = {
        positionX:
          namecardConfiguration.compositionTemplate?.[currentKey]?.positionX,
        positionY:
          namecardConfiguration.compositionTemplate?.[currentKey]?.positionY,
        scale: namecardConfiguration.compositionTemplate?.[currentKey]?.scale,
        scaleX: namecardConfiguration.compositionTemplate?.[currentKey]?.scaleX,
        scaleY: namecardConfiguration.compositionTemplate?.[currentKey]?.scaleY,
      };
      const placementOverride = namecardConfiguration.placementOverride;
      if (
        'positionX' in placementOverride &&
        placementOverride.positionX !== null &&
        placementOverride.positionX !== undefined
      ) {
        placement.positionX = placementOverride.positionX;
      }
      if (
        'positionY' in placementOverride &&
        placementOverride.positionY !== null &&
        placementOverride.positionY !== undefined
      ) {
        placement.positionY = placementOverride.positionY;
      }
      // An override may carry a legacy uniform `scale`, per-axis
      // `scaleX`/`scaleY`, or both. Resolve to per-axis values and write those
      // explicitly: a uniform override must out-rank the composition's per-axis
      // baseline, and reads prefer `scaleX`/`scaleY`, so leaving a stale baseline
      // axis in place would silently win.
      const overrideScaleX =
        placementOverride.scaleX ?? placementOverride.scale;
      const overrideScaleY =
        placementOverride.scaleY ?? placementOverride.scale;
      if (overrideScaleX !== null && overrideScaleX !== undefined) {
        placement.scaleX = overrideScaleX;
      }
      if (overrideScaleY !== null && overrideScaleY !== undefined) {
        placement.scaleY = overrideScaleY;
      }
      if (
        placementOverride.scale !== null &&
        placementOverride.scale !== undefined
      ) {
        placement.scale = placementOverride.scale;
      }

      namecardConfiguration.compositionTemplate = {
        ...namecardConfiguration.compositionTemplate,
        [currentKey]: {
          ...placement,
        },
      };
    }
  }
  return configuration;
}

/**
 * The karaoke brandings referenced by video templates that the branding list
 * doesn't carry yet, so the replace-captions modal can offer a style a template
 * uses even when the showcase doesn't own it.
 *
 * Deduped against `existingIds` *and* within the result: several video templates
 * commonly share one karaoke branding, and a plain filter against the current
 * list let every copy through in a single pass — the modal then showed the same
 * style twice. Global presets are excluded; they reach the list by their own route.
 */
export function collectMissingKaraokeTemplates(
  templates: readonly VideoTemplateConfigured[],
  existingIds: readonly string[],
): KaraokeTemplate[] {
  const seen = new Set(existingIds);
  const collected: KaraokeTemplate[] = [];

  for (const template of templates) {
    const config =
      template.configuration?.captionsConfiguration?.captionsConfig;
    if (!config || config.isGlobalPreset) continue;

    const karaokeTemplate = transformKaraokeConfigToTemplate(config);
    const id = karaokeTemplate?.id as string | undefined;
    if (!karaokeTemplate || !id || seen.has(id)) continue;

    seen.add(id);
    collected.push(karaokeTemplate);
  }

  return collected;
}
